'use client';
// 'use client' required: uses lookup hooks (TanStack Query) and event handlers.

import {
  useVehicleMakes,
  useVehicleModels,
  useVehiclePartNames,
  useVehicleTypes,
} from '@/hooks/useRateMasterLookups';
import type { CreateFieldDef } from './entityTableConfig';

import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CreateFieldInputProps {
  field: CreateFieldDef;
  /** Display-name values keyed by field.key — what actually gets submitted. */
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  /** Numeric lookup ids keyed by field.key — only used internally to
   * drive cascading (e.g. vehicle_type's selected id filters
   * vehicle_make's option list). Not submitted to the backend. */
  selectedIds: Record<string, number | null>;
  onSelectedIdChange: (key: string, id: number | null) => void;
}

/**
 * Renders one create-form field per its `type`. Dropdown kinds
 * ('vehicle-*-select') query GET /lookups/* and submit the option's
 * display name (matching what create_single_row()'s validators expect
 * — human-readable text, not IDs) while tracking the selected id
 * locally so a cascading sibling field (e.g. vehicle-model filtered by
 * vehicle-make) can filter its own options.
 */
export function CreateFieldInput({
  field,
  values,
  onChange,
  selectedIds,
  onSelectedIdChange,
}: CreateFieldInputProps) {
  const cascadeId = field.cascadeFrom ? selectedIds[field.cascadeFrom] ?? null : null;

  const vehicleTypes = useVehicleTypes();
  const vehicleMakes = useVehicleMakes(field.type === 'vehicle-make-select' ? cascadeId : null);
  const vehicleModels = useVehicleModels(field.type === 'vehicle-model-select' ? cascadeId : null);
  const vehiclePartNames = useVehiclePartNames(
    field.type === 'vehicle-part-name-select' && field.cascadeFrom ? cascadeId : null,
  );

  if (field.type === 'boolean') {
    return (
      <Switch
        id={`create-field-${field.key}`}
        checked={Boolean(values[field.key])}
        onCheckedChange={(checked) => onChange(field.key, checked)}
      />
    );
  }

  if (field.type === 'number' || field.type === 'text') {
    return (
      <Input
        id={`create-field-${field.key}`}
        variant={field.type === 'number' ? 'number' : 'text'}
        step={field.step}
        placeholder={field.placeholder}
        value={String(values[field.key] ?? '')}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  }

  // Dropdown kinds
  const options =
    field.type === 'vehicle-type-select'
      ? vehicleTypes.data
      : field.type === 'vehicle-make-select'
        ? vehicleMakes.data
        : field.type === 'vehicle-model-select'
          ? vehicleModels.data
          : vehiclePartNames.data;

  const isLoading =
    field.type === 'vehicle-type-select'
      ? vehicleTypes.isLoading
      : field.type === 'vehicle-make-select'
        ? vehicleMakes.isLoading
        : field.type === 'vehicle-model-select'
          ? vehicleModels.isLoading
          : vehiclePartNames.isLoading;

  const isCascadeDisabled =
    (field.type === 'vehicle-make-select' || field.type === 'vehicle-model-select') &&
    field.cascadeFrom &&
    cascadeId === null;

  return (
    <Select
      value={String(values[field.key] ?? '')}
      onValueChange={(name) => {
        onChange(field.key, name);
        const selected = options?.find((o) => o.name === name);
        onSelectedIdChange(field.key, selected ? Number(selected.id) : null);
      }}
      disabled={isCascadeDisabled || isLoading}
    >
      <SelectTrigger id={`create-field-${field.key}`}>
        <SelectValue
          placeholder={
            isCascadeDisabled
              ? 'Select the field above first'
              : isLoading
                ? 'Loading…'
                : field.placeholder || 'Select…'
          }
        />
      </SelectTrigger>
      <SelectContent>
        {(options ?? []).map((option) => (
          <SelectItem key={option.id} value={option.name}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
